// DashboardTeamCard + DashboardAnnouncementsCard — the two side-by-side
// summary cards that sit beneath the UpNextCard on the program dashboard.
// Each is a small white card with an amber overline; click-through is the
// primary action ("Open team", "View all announcements").


import type { MyAnnouncement } from "../../../lib/announcements";
import type {
  CompetitionStatus as DbCompetitionStatus,
  LeaderboardRow,
  MyTeamPayload,
} from "../../../types/database";
import { Avatar } from "../../ui/Avatar";
import { TeamCard, type TeamCardMember } from "../../aspects/TeamCard";
import { AnnouncementsCard, type AnnouncementsCardItem } from "../../aspects/AnnouncementsCard";
import { PANE_THEMES } from "../../brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;


// ===========================================================================
// Team card
// ===========================================================================

export type DashboardTeamCardProps = {
  myTeam: MyTeamPayload | null;
  isCaptain: boolean;
  maxTeamSize: number;
  /** Team's leaderboard row, if scoring has produced one. */
  ownTeamRow: LeaderboardRow | null;
  /** Total team count for the "#X of N" denominator. */
  totalTeams: number;
  /** Comp status — drives the standing copy for closed comps. Accepts
   *  the wide DB CompetitionStatus plus the UI-synthesized
   *  "closed_to_registration"; pre-active states fall through to the
   *  default "judging"-or-"in progress" branch. */
  competitionStatus: DbCompetitionStatus | "closed_to_registration";
  onOpenTeam: () => void;
  onFindTeam: () => void;
};

export function DashboardTeamCard({
  myTeam,
  isCaptain,
  maxTeamSize,
  ownTeamRow,
  totalTeams,
  competitionStatus,
  onOpenTeam,
  onFindTeam,
}: DashboardTeamCardProps) {
  const onTeam = myTeam != null;

  const judgingHasStarted =
    competitionStatus === "judging" || competitionStatus === "closed";

  const standing = (() => {
    if (!onTeam) return null;
    if (ownTeamRow && ownTeamRow.placement != null) {
      const denom = totalTeams > 0 ? " of " + totalTeams : "";
      const pts =
        ownTeamRow.final_score != null
          ? " · " + ownTeamRow.final_score.toFixed(1) + " pts"
          : "";
      const isFinal = competitionStatus === "closed";
      return {
        label: isFinal ? "FINAL" : "STANDING",
        value: "#" + ownTeamRow.placement + denom + pts,
        tone: "amber" as const,
      };
    }
    if (
      ownTeamRow &&
      ownTeamRow.eliminated_at_stage != null &&
      judgingHasStarted
    ) {
      return {
        label: "STANDING",
        value: "Eliminated at stage " + ownTeamRow.eliminated_at_stage,
        tone: "muted" as const,
      };
    }
    return {
      label: "STANDING",
      value: "Not ranked yet",
      tone: "muted" as const,
    };
  })();

  if (!onTeam) {
    return (
      <TeamCard
        theme={STUDENT_THEME}
        members={[]}
        emptyTitle="Not on a team yet"
        emptySubtitle="Find a public team or build your own."
        ctaLabel="FIND A TEAM"
        onPress={onFindTeam}
        accessibilityLabel="Find or build a team"
      />
    );
  }

  const members: TeamCardMember[] = myTeam!.members.map((m) => ({
    id: m.student_id,
    name: m.full_name ?? "?",
  }));
  return (
    <TeamCard
      theme={STUDENT_THEME}
      teamName={myTeam!.team.name}
      isCaptain={isCaptain}
      members={members}
      maxTeamSize={maxTeamSize}
      standing={standing}
      renderAvatar={(m) => <Avatar name={m.name} size="sm" />}
      ctaLabel="Open team chat"
      onPress={onOpenTeam}
      accessibilityLabel="Open team workspace"
    />
  );
}

// ===========================================================================
// Announcements card
// ===========================================================================

export type DashboardAnnouncementsCardProps = {
  /** Already filtered to this competition + sorted newest first. */
  announcements: MyAnnouncement[];
  onPressAll: () => void;
  onPressOne: (id: string) => void;
};

export function DashboardAnnouncementsCard({
  announcements,
  onPressAll,
  onPressOne,
}: DashboardAnnouncementsCardProps) {
  const unread = announcements.filter((a) => !a.viewed).length;
  const items: AnnouncementsCardItem[] = announcements.map((a) => ({
    id: a.id,
    dateLabel: formatPostedAt(a.published_at ?? a.created_at),
    title: a.title,
    viewed: a.viewed,
  }));
  return (
    <AnnouncementsCard
      theme={STUDENT_THEME}
      items={items}
      unreadCount={unread}
      onPressViewAll={onPressAll}
      onPressItem={onPressOne}
      accessibilityLabel="View all announcements"
    />
  );
}

function formatPostedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return "Today · " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  const isYest =
    d.getFullYear() === yest.getFullYear() &&
    d.getMonth() === yest.getMonth() &&
    d.getDate() === yest.getDate();
  if (isYest) return "Yesterday";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

